// What each square of a fight board is, as the painter draws it (docs/186 EA4). The sim's `Terrain`
// and flags decide; the painter never adds a rule. Off the board and `Void` read as wall to the walls
// beside them (a wall at the board's edge shows its front, not its side), and as nothing to the ground.
import type { TileData } from '../../../core/sim/types';
import type { BoardLook } from '../../../core/encounter/look';

/**
 * A board as the painter takes it. `country`: the country kit an outdoor place stands in (`encounter_stage.country`,
 * EA14); `look`: the rooms' floors and rails the forge laid (`encounter_stage.look`, decoded; EA13).
 */
export interface BoardIn { w: number; h: number; tiles: ReadonlyArray<TileData | undefined>; seed?: number; country?: string; look?: BoardLook }

/**
 * `wall`: a wall square that shows (a front, a top, a tree); `rock`: a wall square with wall on every side
 * (built kits leave it dark, countries fill it with trees); `door`: a doorway in a wall, open or shut;
 * `way`: an opened hidden way (docs/52), drawn as its gap; the rest are grounds.
 */
export type Sq = 'void' | 'floor' | 'rough' | 'water' | 'deep' | 'thicket' | 'chasm' | 'tunnel' | 'wall' | 'rock' | 'door' | 'way';

export class Squares {
  readonly w: number;
  readonly h: number;
  readonly sq: Sq[];
  constructor(readonly board: BoardIn) {
    const { w, h, tiles } = board;
    this.w = w; this.h = h;
    const solid = (x: number, y: number): boolean => {
      if (x < 0 || y < 0 || x >= w || y >= h) return true;
      const t = tiles[y * w + x];
      return !t || t.terrain === 'Wall' || t.terrain === 'Void';
    };
    this.sq = [];
    for (let y = 0; y < h; y++) for (let x = 0; x < w; x++) {
      const t = tiles[y * w + x];
      let s: Sq;
      switch (t?.terrain) {
        case undefined: case 'Void': s = 'void'; break;
        case 'Wall': {
          let all = true;
          for (let j = -1; j <= 1 && all; j++) for (let i = -1; i <= 1; i++) if ((i || j) && !solid(x + i, y + j)) { all = false; break; }
          s = all ? 'rock' : 'wall';
          break;
        }
        case 'Bad': s = 'rough'; break;
        case 'Water': s = 'water'; break;
        case 'Deep': s = 'deep'; break;
        case 'Thicket': s = 'thicket'; break;
        case 'Chasm': s = 'chasm'; break;
        case 'Tunnel': s = 'tunnel'; break;
        // docs/61 EX9: a veil is mist over trail, not a door; docs/52: an opened hidden way is a gap
        case 'Door': s = t.veil ? 'floor' : t.way ? 'way' : 'door'; break;
        default: s = 'floor';
      }
      this.sq.push(s);
    }
  }

  on(x: number, y: number): boolean { return x >= 0 && y >= 0 && x < this.w && y < this.h; }
  at(x: number, y: number): Sq | undefined { return this.on(x, y) ? this.sq[y * this.w + x] : undefined; }
  tile(x: number, y: number): TileData | undefined { return this.on(x, y) ? this.board.tiles[y * this.w + x] : undefined; }
  /** A wall for the walls beside it: wall, rock, the void and off the board. */
  solid(x: number, y: number): boolean { const s = this.at(x, y); return s === undefined || s === 'wall' || s === 'rock' || s === 'void'; }
  /** A wall square on the board (not the void, not off it). */
  isWall(x: number, y: number): boolean { const s = this.at(x, y); return s === 'wall' || s === 'rock'; }
}
